import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../lib/language'
import SiteHeader from '../components/SiteHeader'
import SiteFooter from '../components/SiteFooter'

export default function ContactPage() {
  const { t, language } = useLanguage()
  const isEs = language === 'es'
  const [scrolled, setScrolled]     = useState(false)
  const [showTop, setShowTop]       = useState(false)
  const [form, setForm]             = useState({ name: '', email: '', phone: '', subject: '', message: '' })
  const [submitted, setSubmitted]   = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]           = useState('')

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 60)
      setShowTop(window.scrollY > 400)
    }
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  function scrollToTop() { window.scrollTo({ top: 0, behavior: 'smooth' }) }
  function set(k, v) { setForm(f => ({ ...f, [k]: v })) }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      const response = await fetch('/.netlify/functions/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: 'nancy@notarysigningvault.com',
          from_name: `NSV Contact Form — ${form.name}`,
          subject: `[NSV Contact] ${form.subject} — from ${form.name}`,
          message: `New contact form submission!!\n\nName: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone || 'Not provided'}\nSubject: ${form.subject}\n\nMessage:\n${form.message}\n\n---\nReply directly to: ${form.email}`,
        })
      })
      if (!response.ok) throw new Error('Send failed')
      setSubmitted(true)
    } catch (err) {
      setError(isEs
        ? 'Hubo un error al enviar tu mensaje. Por favor llámanos al (904) 865-0133.'
        : 'There was an error sending your message. Please call us at (904) 865-0133.')
    } finally {
      setSubmitting(false)
    }
  }

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }

    .sr-only { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }
    :focus-visible { outline:2px solid #4D4E8D; outline-offset:3px; border-radius:6px; }

    .lca-hero { padding:64px 44px 40px; text-align:center; background:#E9EAEC; font-family:'Plus Jakarta Sans',sans-serif; }
    .lca-hero-eyebrow { font-size:12px; font-weight:700; letter-spacing:.18em; text-transform:uppercase; color:#8A8F94; margin-bottom:16px; }
    .lca-hero-rule { width:44px; height:3px; background:#4D4E8D; border-radius:2px; margin:0 auto 22px; }
    .lca-hero h1 { font-family:'Plus Jakarta Sans',sans-serif; font-size:clamp(30px,3.6vw,44px); font-weight:800; color:#151715; margin-bottom:14px; line-height:1.14; letter-spacing:-.025em; }
    .lca-hero h1 span { color:#4D4E8D; }
    .lca-hero-sub { font-size:16px; font-weight:500; color:#5C6065; max-width:500px; margin:0 auto; line-height:1.7; }

    .lca-contact-wrap { max-width:1100px; margin:0 auto; padding:56px 44px; display:grid; grid-template-columns:1fr 1.5fr; gap:56px; align-items:start; font-family:'Plus Jakarta Sans',sans-serif; }
    @media(max-width:860px){ .lca-contact-wrap { grid-template-columns:1fr; gap:40px; padding:44px 20px; } }

    .lca-info-section { display:flex; flex-direction:column; gap:32px; }
    .lca-info-label { font-size:11px; letter-spacing:.16em; color:#8A8F94; font-weight:700; text-transform:uppercase; margin-bottom:12px; }
    .lca-info-rule { width:24px; height:3px; background:#4D4E8D; border-radius:2px; margin-bottom:18px; }
    .lca-contact-item { display:flex; align-items:flex-start; gap:14px; margin-bottom:16px; }
    .lca-contact-icon { width:42px; height:42px; border-radius:12px; background:#EEF0F8; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
    .lca-contact-icon svg { width:18px; height:18px; fill:#4D4E8D; }
    .lca-contact-detail-label { font-size:11px; font-weight:600; color:#8A8F94; letter-spacing:.06em; margin-bottom:3px; }
    .lca-contact-detail-value { font-size:14.5px; font-weight:600; color:#151715; text-decoration:none; display:block; transition:color .2s; }
    a.lca-contact-detail-value:hover { color:#4D4E8D; }
    .lca-hours { background:#fff; border:1px solid #E2E4E7; border-radius:14px; padding:18px 22px; box-shadow:0 6px 18px rgba(21,23,21,0.04); }
    .lca-hours-row { display:flex; justify-content:space-between; align-items:center; padding:8px 0; border-bottom:1px solid #EEEFF1; font-size:13px; }
    .lca-hours-row:last-child { border-bottom:none; }
    .lca-hours-day { color:#5C6065; font-weight:500; }
    .lca-hours-time { color:#4D4E8D; font-weight:700; }
    .lca-hours-closed { color:#9AA0A6; font-style:italic; }
    .lca-online-badge { display:inline-flex; align-items:center; gap:8px; background:#EAF2EE; border:1px solid #A9CFBD; border-radius:20px; padding:7px 14px; font-size:12px; color:#3F6B52; font-weight:700; margin-top:16px; }
    .lca-online-dot { width:7px; height:7px; border-radius:50%; background:#5E8A72; animation:pulse 2s infinite; }
    @keyframes pulse { 0%,100%{ opacity:1; } 50%{ opacity:0.4; } }

    .lca-form-title { font-size:26px; font-weight:800; color:#151715; margin-bottom:6px; letter-spacing:-.02em; }
    .lca-form-sub { font-size:13px; font-weight:500; color:#5C6065; margin-bottom:26px; line-height:1.7; }
    .lca-form { display:flex; flex-direction:column; gap:16px; }
    .lca-form-row { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
    @media(max-width:520px){ .lca-form-row { grid-template-columns:1fr; } }
    .lca-field { display:flex; flex-direction:column; gap:6px; }
    .lca-label { font-size:12px; font-weight:600; color:#5C6065; letter-spacing:.02em; }
    .lca-req { color:#4D4E8D; margin-left:2px; }
    .lca-input { background:#fff; border:1px solid #D7DADE; border-radius:10px; padding:12px 14px; font-size:14px; color:#151715; transition:border-color .2s, box-shadow .2s; outline:none; font-family:'Plus Jakarta Sans',sans-serif; }
    .lca-input::placeholder { color:#A2A6AA; }
    .lca-input:focus { border-color:#4D4E8D; box-shadow:0 0 0 3px rgba(77,78,141,0.12); }
    .lca-textarea { resize:vertical; min-height:130px; }

    .lca-submit { background:#4D4E8D; color:#fff; padding:14px 32px; border-radius:12px; font-size:14px; font-weight:700; border:none; cursor:pointer; box-shadow:0 10px 22px rgba(77,78,141,0.22); transition:transform .15s, background .2s; align-self:flex-start; }
    .lca-submit:hover:not(:disabled) { transform:translateY(-2px); background:#41427a; }
    .lca-submit:focus-visible { outline:2px solid #4D4E8D; outline-offset:3px; }
    .lca-submit:disabled { opacity:0.6; cursor:not-allowed; }
    @media(max-width:480px){ .lca-submit { width:100%; text-align:center; } }

    .lca-error { background:#FBEAE8; border:1px solid #E7B0A8; border-radius:10px; padding:12px 16px; font-size:13px; color:#B23A2E; display:flex; gap:8px; align-items:flex-start; }

    .lca-success { background:#EAF2EE; border:1px solid #A9CFBD; border-radius:18px; padding:40px 32px; text-align:center; }
    .lca-success-icon { width:56px; height:56px; border-radius:50%; background:#5E8A72; color:#fff; font-size:30px; display:flex; align-items:center; justify-content:center; margin:0 auto 16px; }
    .lca-success-title { font-size:24px; font-weight:800; color:#151715; margin-bottom:10px; letter-spacing:-.02em; }
    .lca-success-sub { font-size:14px; font-weight:500; color:#5C6065; line-height:1.8; margin-bottom:24px; }
    .lca-success-btn { background:#4D4E8D; color:#fff; padding:12px 26px; border-radius:11px; font-size:13px; font-weight:700; border:none; cursor:pointer; text-decoration:none; display:inline-block; box-shadow:0 10px 22px rgba(77,78,141,0.22); transition:transform .15s, background .2s; }
    .lca-success-btn:hover { transform:translateY(-2px); background:#41427a; }

    .lca-back-top-wrap { background:transparent; display:flex; justify-content:center; padding:0; }
    .lca-back-top { position:fixed; bottom:24px; right:20px; width:46px; height:46px; border-radius:50%; background:#4D4E8D; border:none; cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:18px; color:#fff; font-weight:700; box-shadow:0 8px 20px rgba(77,78,141,0.35); transition:all .3s; opacity:0; pointer-events:none; z-index:150; }
    .lca-back-top.visible { opacity:1; pointer-events:auto; }
    .lca-back-top:hover { transform:translateY(-3px); }
  `

  const PhoneIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
  )
  const EmailIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
  )

  const hours = [
    { day_en: 'Monday – Friday', day_es: 'Lunes – Viernes', time: '9:00 AM – 6:00 PM EST', closed: false },
    { day_en: 'Saturday', day_es: 'Sábado', time: '', closed: true },
    { day_en: 'Sunday', day_es: 'Domingo', time: '', closed: true },
  ]

  return (
    <div style={{ background: '#E9EAEC', minHeight: '100vh', color: '#151715' }}>
      <style>{styles}</style>

      {/* NAV */}
      <SiteHeader />

      <main id="nsv-main" tabIndex={-1}>
        {/* HERO */}
        <section className="lca-hero" aria-labelledby="contact-heading">
          <div className="lca-hero-eyebrow" aria-hidden="true">{isEs ? 'ESTAMOS AQUÍ PARA AYUDARTE' : 'WE ARE HERE TO HELP'}</div>
          <div className="lca-hero-rule" aria-hidden="true" />
          <h1 id="contact-heading">
            {isEs ? 'Contáctanos' : 'Get in'} <span>{isEs ? 'Hoy' : 'Touch'}</span>
          </h1>
          <p className="lca-hero-sub">
            {isEs
              ? '¿Tienes preguntas? ¿Necesitas un notario? Estamos listos para ayudarte — llámanos, escríbenos o completa el formulario.'
              : 'Have questions? Need a notary? We\'re ready to help — call us, email us, or fill out the form below.'}
          </p>
        </section>

        {/* CONTACT GRID */}
        <div className="lca-contact-wrap">
          {/* LEFT — Info */}
          <div className="lca-info-section">
            <div>
              <div className="lca-info-label">{isEs ? 'INFORMACIÓN DE CONTACTO' : 'CONTACT INFORMATION'}</div>
              <div className="lca-info-rule" aria-hidden="true" />
              <div className="lca-contact-item">
                <div className="lca-contact-icon"><PhoneIcon /></div>
                <div>
                  <div className="lca-contact-detail-label">{isEs ? 'TELÉFONO' : 'PHONE'}</div>
                  <a href="tel:+19048650133" className="lca-contact-detail-value" aria-label="Call us at (904) 865-0133">(904) 865-0133</a>
                </div>
              </div>
              <div className="lca-contact-item">
                <div className="lca-contact-icon"><EmailIcon /></div>
                <div>
                  <div className="lca-contact-detail-label">{isEs ? 'CORREO ELECTRÓNICO' : 'EMAIL'}</div>
                  <a href="mailto:nancy@notarysigningvault.com" className="lca-contact-detail-value" aria-label="Email us">nancy@notarysigningvault.com</a>
                </div>
              </div>
            </div>
            {/* Hours */}
            <div>
              <div className="lca-info-label">{isEs ? 'HORARIO DE OFICINA' : 'OFFICE HOURS'}</div>
              <div className="lca-info-rule" aria-hidden="true" />
              <div className="lca-hours" role="list" aria-label={isEs ? 'Horario de oficina' : 'Office hours'}>
                {hours.map((h, i) => (
                  <div key={i} className="lca-hours-row" role="listitem">
                    <span className="lca-hours-day">{isEs ? h.day_es : h.day_en}</span>
                    {h.closed
                      ? <span className="lca-hours-closed">{isEs ? 'Cerrado' : 'Closed'}</span>
                      : <span className="lca-hours-time">{h.time}</span>}
                  </div>
                ))}
              </div>
              <div className="lca-online-badge" role="note">
                <div className="lca-online-dot" aria-hidden="true" />
                {isEs ? 'En línea 24/7/365' : 'Online 24/7/365'}
              </div>
              <div role="note" style={{ marginTop:12, fontSize:12, color:'#7C8085', fontStyle:'italic', textAlign:'center' }}>
                {isEs
                  ? '📍 Brindando cobertura notarial móvil y remota en los 50 estados de EE. UU.'
                  : '📍 Providing mobile and remote notary coverage across all 50 U.S. states.'}
              </div>
            </div>
          </div>

          {/* RIGHT — Form */}
          <div>
            {submitted ? (
              <div className="lca-success" role="alert" aria-live="assertive">
                <div className="lca-success-icon" aria-hidden="true">✓</div>
                <div className="lca-success-title">{isEs ? '¡Mensaje Enviado!' : 'Message Sent!'}</div>
                <p className="lca-success-sub">
                  {isEs
                    ? 'Gracias por contactarnos, Nancy se comunicará contigo dentro de 1 día hábil. ¡Que tengas un excelente día! 😊'
                    : 'Thank you for reaching out! Nancy will get back to you within 1 business day. Have a wonderful day! 😊'}
                </p>
                <button className="lca-success-btn" onClick={() => { setSubmitted(false); setForm({ name:'', email:'', phone:'', subject:'', message:'' }) }}>
                  {isEs ? 'Enviar otro mensaje' : 'Send another message'}
                </button>
              </div>
            ) : (
              <>
                <h2 className="lca-form-title">{isEs ? 'Envíanos un Mensaje' : 'Send Us a Message'}</h2>
                <p className="lca-form-sub">
                  {isEs
                    ? 'Completa el formulario y nos comunicaremos contigo dentro de 1 día hábil.'
                    : 'Fill out the form and we\'ll get back to you within 1 business day.'}
                </p>
                {error && (
                  <div className="lca-error" role="alert" aria-live="assertive" style={{ marginBottom: 16 }}>
                    <span aria-hidden="true">⚠️</span><span>{error}</span>
                  </div>
                )}
                <form className="lca-form" onSubmit={handleSubmit} noValidate aria-label={isEs ? 'Formulario de contacto' : 'Contact form'}>
                  <p style={{ fontSize:11, color:'#8A8F94', marginBottom:4 }}>
                    <span aria-hidden="true" style={{ color:'#4D4E8D' }}>*</span> {isEs ? 'Campos requeridos' : 'Required fields'}
                  </p>
                  <div className="lca-form-row">
                    <div className="lca-field">
                      <label className="lca-label" htmlFor="contact-name">
                        {isEs ? 'Nombre completo' : 'Full name'}
                        <span className="lca-req" aria-hidden="true"> *</span>
                        <span className="sr-only">{isEs ? ' (requerido)' : ' (required)'}</span>
                      </label>
                      <input id="contact-name" className="lca-input" type="text" placeholder={isEs ? 'Tu nombre completo' : 'Your full name'} value={form.name} onChange={e => set('name', e.target.value)} required aria-required="true" />
                    </div>
                    <div className="lca-field">
                      <label className="lca-label" htmlFor="contact-email">
                        {isEs ? 'Correo electrónico' : 'Email address'}
                        <span className="lca-req" aria-hidden="true"> *</span>
                        <span className="sr-only">{isEs ? ' (requerido)' : ' (required)'}</span>
                      </label>
                      <input id="contact-email" className="lca-input" type="email" placeholder="you@example.com" value={form.email} onChange={e => set('email', e.target.value)} required aria-required="true" />
                    </div>
                  </div>
                  <div className="lca-form-row">
                    <div className="lca-field">
                      <label className="lca-label" htmlFor="contact-phone">{isEs ? 'Teléfono' : 'Phone number'}</label>
                      <input id="contact-phone" className="lca-input" type="tel" placeholder="(xxx) xxx-xxxx" value={form.phone} onChange={e => set('phone', e.target.value)} />
                    </div>
                    <div className="lca-field">
                      <label className="lca-label" htmlFor="contact-subject">
                        {isEs ? 'Asunto' : 'Subject'}
                        <span className="lca-req" aria-hidden="true"> *</span>
                        <span className="sr-only">{isEs ? ' (requerido)' : ' (required)'}</span>
                      </label>
                      <select id="contact-subject" className="lca-input" value={form.subject} onChange={e => set('subject', e.target.value)} required aria-required="true" style={{ cursor:'pointer', color:'#151715' }}>
                        <option value="" style={{ color:'#9AA0A6' }}>{isEs ? 'Selecciona un tema...' : 'Select a topic...'}</option>
                        <option value="request_notary" style={{ color:'#151715' }}>{isEs ? 'Solicitar un Notario' : 'Request a Notary'}</option>
                        <option value="join_network" style={{ color:'#151715' }}>{isEs ? 'Unirse a Nuestra Red' : 'Join Our Network'}</option>
                        <option value="company_services" style={{ color:'#151715' }}>{isEs ? 'Servicios para Empresas' : 'Company Signing Services'}</option>
                        <option value="pricing" style={{ color:'#151715' }}>{isEs ? 'Preguntas sobre Precios' : 'Pricing Questions'}</option>
                        <option value="general" style={{ color:'#151715' }}>{isEs ? 'Pregunta General' : 'General Question'}</option>
                        <option value="veteran" style={{ color:'#151715' }}>{isEs ? 'Descuento para Veteranos' : 'Veteran Discount'}</option>
                        <option value="other" style={{ color:'#151715' }}>{isEs ? 'Otro' : 'Other'}</option>
                      </select>
                    </div>
                  </div>
                  <div className="lca-field">
                    <label className="lca-label" htmlFor="contact-message">
                      {isEs ? 'Mensaje' : 'Message'}
                      <span className="lca-req" aria-hidden="true"> *</span>
                      <span className="sr-only">{isEs ? ' (requerido)' : ' (required)'}</span>
                    </label>
                    <textarea id="contact-message" className="lca-input lca-textarea" placeholder={isEs ? '¿Cómo podemos ayudarte?' : 'How can we help you?'} value={form.message} onChange={e => set('message', e.target.value)} required aria-required="true" />
                  </div>
                  <button className="lca-submit" type="submit" disabled={submitting} aria-label={isEs ? "Enviar Mensaje" : "Send Message"} aria-busy={submitting}>
                    {submitting ? (isEs ? 'Enviando...' : 'Sending...') : (isEs ? 'Enviar Mensaje' : 'Send Message')}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>

      {/* BACK TO TOP */}
      <div className="lca-back-top-wrap">
        <button className={`lca-back-top${showTop ? ' visible' : ''}`} onClick={scrollToTop} aria-label="Scroll back to top" aria-hidden={!showTop} tabIndex={showTop ? 0 : -1}>↑</button>
      </div>

      {/* FOOTER */}
      <SiteFooter />
    </div>
  )
}
